import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Modal,
  Text,
  Paper,
  Group,
  Image,
  Loader,
  Center,
  ScrollArea,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Box,
  Grid,
  Select 
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { 
  IconEye, 
  IconInfoCircle, 
  IconRefresh, 
  IconPhoto, 
  IconSchool,
  IconChefHat,
  IconCalendar,
  IconFilter 
} from "@tabler/icons-react";
import api from "../../services/api";

// Helper: Pastikan URL foto lengkap
const getFotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `https://be-gizimon-sibm3.karyakreasi.id/storage/${url}`;
};

export default function AdminKonfirmasiPenerimaanPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- 3. State untuk Filter ---
  const [filterSekolah, setFilterSekolah] = useState(null);

  // Modal State
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/konfirmasi-penerimaan");
      const hasil = response.data.data || response.data;
      setData(Array.isArray(hasil) ? hasil : []);
      // Reset filter saat refresh data
      // setFilterSekolah(null); 
    } catch (error) {
      console.error("Gagal Load Data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 4. LOGIKA FILTERING ---

  // A. Ambil daftar sekolah unik untuk opsi Dropdown
  const schoolOptions = Array.from(
    new Set(
      data
        .map((item) => item.nama_sekolah) // Ambil nama sekolah
        .filter((nama) => nama) // Hapus yang null
    )
  ).map((nama) => ({ value: nama, label: nama }));

  // B. Filter Data List berdasarkan pilihan dropdown
  const filteredData = data.filter((item) => {
    if (!filterSekolah) return true; // Tampilkan semua jika filter kosong
    return item.nama_sekolah === filterSekolah;
  });

  // --- HANDLERS ---
  const handleViewDetail = (item) => {
    setSelectedItem(item);
    open();
  };

  // --- RENDER UI ---
  if (loading) return <Center h={300}><Loader /></Center>;

  // --- GENERATE ROWS DARI FILTERED DATA ---
  const rows = filteredData.map((item) => (
    <Table.Tr key={item.id}>
      {/* Kolom Waktu */}
      <Table.Td>
          <Text fz="sm" fw={500}>
              {new Date(item.waktu_konfirmasi).toLocaleDateString("id-ID", {
                  day: 'numeric', month: 'short', year: 'numeric'
              })}
          </Text>
          <Text fz="xs" c="dimmed">
              {new Date(item.waktu_konfirmasi).toLocaleTimeString("id-ID", {
                  hour: '2-digit', minute:'2-digit'
              })}
          </Text>
      </Table.Td>

      {/* Kolom Sekolah */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon variant="light" color="blue" radius="xl" size="lg">
                <IconSchool size={18}/>
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>{item.nama_sekolah}</Text>
                <Text fz="xs" c="dimmed">Penerima</Text>
            </div>
        </Group>
      </Table.Td>
      
      {/* Kolom Menu & Penyedia */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon variant="light" color="orange" radius="xl" size="lg">
                <IconChefHat size={18}/>
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>{item.nama_menu}</Text>
                <Text fz="xs" c="dimmed">{item.nama_penyedia}</Text>
            </div>
        </Group>
      </Table.Td>
      
      {/* Kolom Status */}
      <Table.Td>
        <Badge color="green" variant="light">Diterima</Badge>
      </Table.Td>
      
      {/* Kolom Aksi */}
      <Table.Td>
        <Group gap="xs" justify="flex-end">
            <Tooltip label="Lihat Bukti Foto">
                <ActionIcon variant="subtle" color="blue" onClick={() => handleViewDetail(item)}>
                    <IconEye size={16} />
                </ActionIcon>
            </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div style={{ padding: 20 }}>
      
      <Group justify="space-between" mb="md">
        <div>
            <Title order={2}>Monitoring Penerimaan</Title>
            <Text c="dimmed" size="sm">Verifikasi bukti penerimaan makanan dari sekolah</Text>
        </div>
        <Button variant="light" onClick={fetchData} leftSection={<IconRefresh size={16}/>}>
            Refresh
        </Button>
      </Group>

      {/* --- 5. UI FILTER --- */}
      <Paper p="md" mb="md" withBorder radius="md" bg="gray.0">
         <Group>
            <Select 
                placeholder="Filter Sekolah"
                leftSection={<IconFilter size={16} />}
                data={schoolOptions}
                value={filterSekolah}
                onChange={setFilterSekolah}
                clearable
                searchable
                nothingFoundMessage="Sekolah tidak ditemukan"
                style={{ width: 300 }}
            />
            {filterSekolah && (
                <Text size="sm" c="dimmed">
                    Hasil filter: <b>{filteredData.length}</b> data ditemukan
                </Text>
            )}
         </Group>
      </Paper>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        {data.length === 0 ? (
            <Center p="xl" c="dimmed">
                <Group>
                    <IconInfoCircle size={20}/>
                    <Text>Belum ada data konfirmasi penerimaan.</Text>
                </Group>
            </Center>
        ) : (
            <ScrollArea>
                <Table highlightOnHover verticalSpacing="sm">
                    <Table.Thead bg="gray.0">
                    <Table.Tr>
                        <Table.Th>Waktu Terima</Table.Th>
                        <Table.Th>Sekolah</Table.Th>
                        <Table.Th>Menu & Penyedia</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th ta="right">Aksi</Table.Th>
                    </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.length > 0 ? rows : (
                            <Table.Tr>
                                <Table.Td colSpan={5}>
                                    <Center p="xl" c="dimmed">
                                        Tidak ada data untuk sekolah <b>{filterSekolah}</b>.
                                    </Center>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        )}
      </Paper>

      {/* --- MODAL DETAIL (Sama seperti sebelumnya) --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Group><IconPhoto size={20} color="#228be6"/><Text fw={700}>Bukti Penerimaan</Text></Group>}
        centered
        size="lg"
      >
        {selectedItem && (
          <Box>
            <Paper withBorder p="sm" mb="md" bg="gray.0" radius="md">
                <Grid>
                    <Grid.Col span={6}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">Sekolah</Text>
                        <Text fw={500}>{selectedItem.nama_sekolah}</Text>
                    </Grid.Col>
                    <Grid.Col span={6} style={{textAlign:'right'}}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">Waktu</Text>
                        <Group justify="flex-end" gap={5}>
                            <IconCalendar size={14}/> 
                            <Text size="sm">
                                {new Date(selectedItem.waktu_konfirmasi).toLocaleString("id-ID")}
                            </Text>
                        </Group>
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">Catatan Sekolah</Text>
                        <Text size="sm" style={{ fontStyle: 'italic' }}>
                            "{selectedItem.catatan || 'Tidak ada catatan khusus.'}"
                        </Text>
                    </Grid.Col>
                </Grid>
            </Paper>

            <Text fw={500} mb="xs" size="sm">Foto Dokumentasi:</Text>
            <Center p="sm" style={{ border: '1px dashed #ccc', borderRadius: 8, background: '#f8f9fa', minHeight: 200 }}>
                {selectedItem.foto_bukti_url ? (
                    <Image 
                        src={getFotoUrl(selectedItem.foto_bukti_url)} 
                        radius="md" 
                        alt="Bukti Penerimaan"
                        fit="contain"
                        h={350}
                        fallbackSrc="https://placehold.co/600x400?text=Foto+Tidak+Ditemukan"
                    />
                ) : (
                    <Group c="dimmed">
                        <IconInfoCircle size={20}/>
                        <Text size="sm">Tidak ada foto yang dilampirkan.</Text>
                    </Group>
                )}
            </Center>

            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={close}>Tutup</Button>
            </Group>
          </Box>
        )}
      </Modal>

    </div>
  );
}